import { ListTypes } from '$shared/components';
import { EntityParameterType } from '$shared/constants/EntityParameterType';
import { parseDynamicData } from '$shared/helpers/utils';
import type DatasetScanParam from '../DatasetScanParam';

export class DatasetMatchRecord {
  protected record: ObjectType<any>;
  public positiveParameters: DatasetScanParam[];
  public negativeParameters: DatasetScanParam[];

  static readonly IgnoreKeys = ['id', 'hash'];

  constructor(record: ObjectType<any>) {
    this.record = record;
  }

  get namePrefix(): string {
    const prefix = this.record?.namePrefix || this.record?.prefix || '';
    if (prefix && prefix === '--') {
      return '';
    }
    return prefix;
  }

  getName(): string {
    if (this.record?.firstName) {
      const name = [
        this.namePrefix,
        this.record?.firstName,
        this.record?.middleName,
        this.record?.lastName
      ]
        .filter(Boolean)
        .join(' ');
      return name.trim();
    }

    if (this.record?.fullName) {
      return this.record.fullName;
    }

    if (typeof this.record?.name === 'object') {
      const givenName = this.record?.name?.givenName;
      const tempName: string[] = givenName ? [...givenName] : [];
      this.record.name.familyName && tempName.push(this.record.name.familyName);
      return tempName.filter(Boolean).join(' ');
    }

    if (this.record?.name) {
      return this.record.name;
    }
    return '';
  }

  public toList(): IDataList {
    const record = { ...this.record };

    // Remove unneeded data
    DatasetMatchRecord.IgnoreKeys.forEach((ignoreKey) => {
      if (record[ignoreKey]) {
        delete record[ignoreKey];
      }
    });

    const tempResult: IDataList = { name: this.getName(), ...record };
    Object.entries(tempResult).forEach(([key, value]) => {
      const newValue = parseDynamicData(value);
      if (newValue !== value) {
        tempResult[key] = newValue;
      }

      if (Array.isArray(value) && value.length !== 0) {
        if (typeof value[0] === 'string') {
          tempResult[key] = {
            component: ListTypes.SimpleArrayListType,
            props: {
              value: value
            }
          };
        } else if (typeof value[0] === 'object') {
          tempResult[key] = {
            component: ListTypes.ArrayObjectListType,
            props: {
              value: value
            }
          };
        }
      } else if (typeof value === 'object') {
        tempResult[key] = {
          component: ListTypes.ObjectListType,
          props: {
            value: value
          }
        };
      }
    });

    return { ...tempResult };
  }

  public highlightRecordPropWithParameters(dataList: IDataList): void {
    const highlightResultProp = (param: DatasetScanParam, icon: string, show = true): void => {
      const resultKey = this.getResultKeyByType(param.type!);
      if (!resultKey) return;

      const value = dataList[resultKey];
      if (!value || !['string', 'number'].includes(typeof value)) return;

      dataList[resultKey] = { actualValue: value, icon, showTooltip: show };
    };

    // Parsing positive params
    this.positiveParameters?.forEach((param) => highlightResultProp(param, 'icon-check', false));

    // Parsing negative params
    this.negativeParameters?.forEach((param) => highlightResultProp(param, 'icon-alert-triangle'));
  }

  // It will be overriden on sub classes
  protected getResultKeyByType(type: EntityParameterType): string | undefined {
    const nameKeys = DatasetMatchRecord.getNameKeys();
    if (nameKeys.includes(type)) {
      return 'name';
    }
  }

  static getNameKeys(): EntityParameterType[] {
    return [EntityParameterType.ProviderName, EntityParameterType.FacilityName];
  }
}
